import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import { Button } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import Drawer from '../../../components/Drawer'
import GrantContent from '../../../components/GrantContent'
import * as Permisos from '../../../utils/Permisos'
import {
    BITACORA_FILTERS,
    chipClass,
    fiscalLabel,
    formatActivityDate,
    formatSlashDate,
    formatSpokenDate,
    formatWithAge,
    initials,
    labelize,
    money,
    NOTE_TYPES,
    noteMeta,
    RamoMark,
    statusKey,
} from '../common/customer-common'
import { isBusy, useCustomerDrawerModel } from './customer-drawer-model'
import type { CustomerDrawerProps } from './customer-drawer-model'

export type { CustomerDrawerProps, CustomerDrawerTab } from './customer-drawer-model'

const actionClass = 'inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-k-line-strong bg-transparent px-3 text-[13px] font-bold text-k-ink no-underline hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const Field = ({ label, children, wide }: { label: string; children?: React.ReactNode; wide?: boolean }) => (
    <div className={wide ? 'sm:col-span-2' : ''}>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-surface px-[18px] py-4">
        <h3 className="m-0 text-[13px] font-extrabold uppercase tracking-[0.06em] text-k-muted">{title}</h3>
        <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-2">{children}</dl>
    </section>
)

const fileExt = (name?: string | null) => String(name || '').split('.').pop()?.slice(0, 3).toUpperCase() || 'FILE'

const extTone = (ext: string) => {
    if (ext === 'PDF') return 'bg-k-danger-soft text-k-danger-ink'
    if (ext === 'JPG' || ext === 'PNG' || ext === 'JPE') return 'bg-tone-blue text-tone-blue-ink'
    if (ext === 'XLS' || ext === 'XLSX') return 'bg-k-ok-soft text-k-ok'
    return 'bg-k-line text-k-ink-2'
}

const polizaStatusClass = (status?: string | null) => {
    const value = statusKey(status)
    if (['vigente', 'renovada', 'anticipada'].includes(value)) return 'bg-k-ok-soft text-k-ok'
    if (['vencida', 'cancelada', 'precancelada', 'desprotegido', 'eliminada', 'terminada'].includes(value)) return 'bg-k-danger-soft text-k-danger-ink'
    return 'bg-tone-orange text-tone-orange-ink'
}

const Pulse = ({ className }: { className: string }) => <div className={`animate-pulse rounded bg-k-line ${className}`} />

const EmptyState = ({ title, hint }: { title: string; hint?: string }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-k-line bg-k-surface px-4 py-10 text-center">
        <p className="m-0 text-sm font-bold text-k-ink">{title}</p>
        {hint && <p className="mb-0 mt-1 text-xs text-k-muted">{hint}</p>}
    </div>
)

const PolizasSkeleton = () => (
    <div className="flex flex-col gap-2.5" aria-busy="true" aria-label="Cargando pólizas">
        {[1, 2, 3].map((row) => (
            <div key={row} className="flex items-center gap-3.5 rounded-2xl border border-k-line bg-k-surface px-4 py-3.5">
                <Pulse className="h-10 w-10 shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                    <Pulse className="h-4 w-44" />
                    <Pulse className="mt-2 h-3 w-56" />
                </div>
                <Pulse className="h-4 w-16" />
                <Pulse className="h-5 w-16 rounded-full" />
            </div>
        ))}
    </div>
)

const BitacoraSkeleton = () => (
    <div className="flex flex-col" aria-busy="true" aria-label="Cargando bitácora">
        {[1, 2, 3, 4].map((row) => (
            <div key={row} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                    <Pulse className="h-[34px] w-[34px] rounded-[10px]" />
                    {row < 4 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                </div>
                <div className="min-w-0 flex-1 pb-[18px] pt-1">
                    <div className="flex justify-between gap-2">
                        <Pulse className="h-3.5 w-24" />
                        <Pulse className="h-3 w-16" />
                    </div>
                    <Pulse className="mt-2 h-14 w-full rounded-xl" />
                </div>
            </div>
        ))}
    </div>
)

const AdjuntosSkeleton = () => (
    <div className="flex flex-col gap-2" aria-busy="true" aria-label="Cargando adjuntos">
        {[1, 2, 3, 4].map((row) => (
            <div key={row} className="flex items-center gap-3 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                <Pulse className="h-11 w-9 rounded-[7px]" />
                <div className="min-w-0 flex-1">
                    <Pulse className="h-3.5 w-40" />
                    <Pulse className="mt-1.5 h-3 w-24" />
                </div>
                <Pulse className="h-9 w-9 rounded-[10px]" />
                <Pulse className="h-9 w-9 rounded-[10px]" />
            </div>
        ))}
    </div>
)

const CustomerDrawer = ({ clienteId, onClose, onEdit, initialTab = 'general' }: CustomerDrawerProps) => {
    const {
        cliente,
        loading,
        error,
        tab,
        toast,
        tabs,
        name,
        moral,
        activo,
        direccion,
        sepomex,
        apellidos,
        altaSpoken,
        clienteDesde,
        vigentesCount,
        proximaVigencia,
        vigentesState,
        vigentesError,
        polizas,
        polizasOrdenadas,
        polizasState,
        polizasError,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes,
        noteType,
        note,
        notePlaceholder,
        savingNote,
        adjuntos,
        adjuntosState,
        adjuntosError,
        uploading,
        confirmDeleteId,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        setConfirmDeleteId,
        reload,
        addNote,
        openFile,
        uploadFiles,
        deleteAdjunto,
    } = useCustomerDrawerModel({ clienteId, initialTab })
    const vigentesReady = vigentesState === 'ready'

    return (
        <>
            <Drawer open={Boolean(clienteId)} onClose={onClose} bare width={680}>
                <div className="flex h-full min-h-0 flex-col" role="dialog" aria-modal="true" aria-label={name ? `Detalle de ${name}` : 'Detalle de cliente'}>
                    <div className="flex shrink-0 flex-col gap-4 border-b border-k-line px-6 pt-5">
                        <div className="flex items-start gap-3.5">
                            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[18px] text-xl font-extrabold text-white" style={{ background: 'linear-gradient(135deg, #1FC8C1, #3E6BE6 55%, #8B3FC9)' }}>
                                {initials(name)}
                            </span>
                            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                                <h2 className="m-0 truncate text-[21px] font-extrabold tracking-[-0.02em] text-k-ink">{name || 'Detalle de cliente'}</h2>
                                {cliente && !loading && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-k-muted">
                                            <span className="font-bold tabular-nums text-k-ink-2">{cliente.rfc || '—'}</span>
                                            <span>Expediente {cliente.id}</span>
                                            {clienteDesde && <span>Cliente desde {clienteDesde}</span>}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${moral ? 'bg-tone-purple text-tone-purple-ink' : 'bg-k-accent-soft text-k-accent-ink'}`}>{fiscalLabel(cliente.fiscal)}</span>
                                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${activo ? 'bg-k-ok-soft text-k-ok' : 'bg-k-danger-soft text-k-danger-ink'}`}>{labelize(cliente.status) || '—'}</span>
                                            {cliente.esRobot && <span className="rounded-full bg-tone-orange px-[9px] py-[3px] text-xs font-bold text-tone-orange-ink">Por validar</span>}
                                        </div>
                                    </>
                                )}
                            </div>
                            <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-transparent text-k-ink hover:bg-k-hover" onClick={onClose}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </button>
                        </div>

                        {cliente && !loading && (
                            <div className="flex flex-wrap justify-end gap-2">
                                <Link className={actionClass} to={`/clientes/${cliente.id}`} aria-label={`Ver ${name || 'cliente'}`}>
                                    <VisibilityOutlinedIcon style={{ fontSize: 15 }} />Ver
                                </Link>
                                <GrantContent permisos={[Permisos.CLIENTES_W]}>
                                    <button type="button" className={actionClass} onClick={() => onEdit?.(cliente.id)}>
                                        <EditOutlinedIcon style={{ fontSize: 15 }} />Editar
                                    </button>
                                </GrantContent>
                            </div>
                        )}

                        <div role="tablist" aria-label="Secciones del cliente" className="-mx-2 flex gap-0.5 overflow-x-auto">
                            {tabs.map((item) => {
                                const selected = tab === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={selected}
                                        className={`inline-flex min-h-12 items-center gap-2 whitespace-nowrap border-0 border-b-[3px] bg-transparent px-3 text-sm ${selected ? 'border-k-primary font-extrabold text-k-ink' : 'border-transparent font-semibold text-k-muted'}`}
                                        onClick={() => setTab(item.id)}
                                    >
                                        {item.label}
                                        {item.count != null && (
                                            <span className={`inline-flex h-5 min-w-[22px] items-center justify-center rounded-[10px] px-1.5 text-xs font-bold ${selected ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line text-k-ink-2'}`}>
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div role="tabpanel" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-k-bg px-6 py-5">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-12">
                                <RefreshIcon className="animate-spin text-k-primary" style={{ fontSize: 40 }} />
                                <p className="text-sm text-k-muted">Cargando detalle...</p>
                            </div>
                        )}

                        {error && !loading && (
                            <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                                <div className="flex items-center gap-2">
                                    <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 20 }} />
                                    <p className="text-sm text-k-danger-ink">{error}</p>
                                </div>
                                <Button className="mt-3" variant="contained" onClick={reload}>Reintentar</Button>
                            </div>
                        )}

                        {cliente && !loading && tab === 'general' && (
                            <>
                                {cliente.esRobot && (
                                    <div className="flex items-start gap-2 rounded-lg bg-tone-orange px-4 py-3 text-tone-orange-ink">
                                        <WarningRoundedIcon style={{ fontSize: 18 }} />
                                        <p className="text-sm">Cliente creado automáticamente con la sincronización dalala. Conviene validar sus datos.</p>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Pólizas vigentes</span>
                                        <span className="text-lg font-extrabold tabular-nums">{vigentesReady ? vigentesCount : (isBusy(vigentesState) ? '…' : '—')}</span>
                                        <span className="text-xs font-semibold text-k-muted">{vigentesReady ? 'Pólizas en vigor' : ' '}</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Próximo vencimiento</span>
                                        <span className="text-lg font-extrabold tabular-nums">{vigentesReady ? (formatSpokenDate(proximaVigencia) || '—') : (isBusy(vigentesState) ? '…' : '—')}</span>
                                        <span className="text-xs font-semibold text-k-muted">De las pólizas vigentes</span>
                                    </div>
                                    <div className="flex flex-col gap-1 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                        <span className="text-xs font-semibold text-k-muted">Alta</span>
                                        <span className="text-lg font-extrabold tabular-nums">{altaSpoken || '—'}</span>
                                        <span className={`text-xs font-semibold ${activo ? 'text-k-ok' : 'text-k-danger-ink'}`}>{labelize(cliente.status) || '—'}</span>
                                    </div>
                                </div>
                                {vigentesError && <p className="m-0 text-sm text-k-danger-ink">{vigentesError}</p>}
                                <Section title="Datos personales">
                                    <Field label="Personalidad">{fiscalLabel(cliente.fiscal)}</Field>
                                    <Field label="Alias">{cliente.alias}</Field>
                                    <Field label="Nombres">{cliente.nombres}</Field>
                                    <Field label="Apellidos">{apellidos}</Field>
                                    <Field label="Fecha de nacimiento">{formatWithAge(cliente.fecNacimiento)}</Field>
                                    <Field label="Sexo">{labelize(cliente.sexo)}</Field>
                                    <Field label="Estado civil">{labelize(cliente.edoCivil)}</Field>
                                    <Field label="RFC">{cliente.rfc}</Field>
                                </Section>
                                <Section title="Contacto">
                                    <Field label="Correo" wide>
                                        {cliente.correos?.length
                                            ? cliente.correos.map((correo, index) => (
                                                <a key={correo.id || index} href={`mailto:${correo.correo}`} className="block font-semibold text-k-primary no-underline">{correo.correo}</a>
                                            ))
                                            : '—'}
                                    </Field>
                                    <Field label="Celular">
                                        {cliente.celulares?.length
                                            ? cliente.celulares.map((cel, index) => (
                                                <a key={cel.id || index} href={`tel:${cel.numero}`} className="block font-semibold text-k-primary no-underline">{cel.numero}</a>
                                            ))
                                            : '—'}
                                    </Field>
                                </Section>
                                <Section title="Domicilio">
                                    <Field label="Calle y número" wide>{direccion?.direccion}</Field>
                                    <Field label="Colonia">{direccion?.colonia}</Field>
                                    <Field label="CP">{direccion?.cp}</Field>
                                    <Field label="Municipio">{sepomex?.municipio}</Field>
                                    <Field label="Estado">{sepomex?.estado}</Field>
                                </Section>
                            </>
                        )}

                        {cliente && !loading && tab === 'polizas' && (
                            <>
                                {isBusy(polizasState) && <PolizasSkeleton />}
                                {polizasError && <p className="m-0 text-sm text-k-danger-ink">{polizasError}</p>}
                                {polizasState === 'ready' && polizas.length === 0 && (
                                    <EmptyState title="No hay pólizas" hint="Este cliente aún no tiene pólizas registradas." />
                                )}
                                {polizasState === 'ready' && polizasOrdenadas.map((poliza) => (
                                    <Link key={poliza.polizaId} to={`/polizas/${poliza.polizaId}`} className="flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-2xl border border-k-line bg-k-surface px-4 py-3.5 text-k-ink no-underline hover:bg-k-hover">
                                        <RamoMark ramo={poliza.ramo} boxed />
                                        <span className="flex min-w-[200px] flex-1 flex-col gap-0.5">
                                            <span className="text-[15px] font-extrabold">{poliza.concepto || poliza.ramo || 'Póliza'}</span>
                                            <span className="text-xs tabular-nums text-k-muted">{poliza.noPoliza || '—'} · {poliza.aseguradora || '—'} · vence {formatSlashDate(poliza.fechaVigenciaA) || '—'}</span>
                                        </span>
                                        <span className="text-sm font-extrabold tabular-nums">{money(poliza.prima, poliza.moneda)}</span>
                                        <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${polizaStatusClass(poliza.status)}`}>{labelize(poliza.status) || '—'}</span>
                                    </Link>
                                ))}
                            </>
                        )}

                        {cliente && !loading && tab === 'bitacora' && (
                            <>
                                <GrantContent permisos={[Permisos.BITACORA_W]}>
                                    <section className="flex flex-col gap-2.5 rounded-2xl border border-k-line bg-k-surface p-3.5">
                                        <div role="radiogroup" aria-label="Tipo de registro" className="flex flex-wrap gap-1.5">
                                            {NOTE_TYPES.map((item) => (
                                                <button key={item.id} type="button" role="radio" aria-checked={noteType === item.id} className={chipClass(noteType === item.id)} onClick={() => setNoteType(item.id)}>
                                                    <item.Icon style={{ fontSize: 16 }} />
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                        <label className="flex flex-col">
                                            <span className="sr-only">Nuevo registro de bitácora</span>
                                            <textarea rows={3} value={note} placeholder={notePlaceholder} className="resize-y rounded-xl border border-k-line-strong bg-k-bg px-3 py-2.5 text-sm text-k-ink" onChange={(event) => setNote(event.target.value)} />
                                        </label>
                                        <div className="flex items-center justify-between gap-2.5">
                                            <span className="text-xs text-k-muted">Visible para tu equipo · se guarda con fecha</span>
                                            <button type="button" disabled={!note.trim() || savingNote} className={`h-10 rounded-[10px] border-0 px-3.5 text-[13px] font-bold ${note.trim() && !savingNote ? 'cursor-pointer bg-k-primary text-white' : 'cursor-not-allowed bg-k-line text-k-muted-2'}`} onClick={addNote}>
                                                {savingNote ? 'Guardando…' : 'Agregar a bitácora'}
                                            </button>
                                        </div>
                                    </section>
                                </GrantContent>
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bitácora">
                                    {BITACORA_FILTERS.map((item) => {
                                        const Icon = NOTE_TYPES.find((type) => type.filter === item.id)?.Icon
                                        return (
                                            <button key={item.id} type="button" aria-pressed={bitacoraFilter === item.id} className={chipClass(bitacoraFilter === item.id)} onClick={() => setBitacoraFilter(item.id)}>
                                                {Icon && <Icon style={{ fontSize: 16 }} />}
                                                {item.label}
                                            </button>
                                        )
                                    })}
                                </div>
                                {isBusy(bitacoraState) && <BitacoraSkeleton />}
                                {bitacoraError && <p className="m-0 text-sm text-k-danger-ink">{bitacoraError}</p>}
                                {bitacoraState === 'ready' && visibleNotes.length === 0 && (
                                    <EmptyState
                                        title="No hay registros"
                                        hint={bitacoraFilter === 'todo' ? 'Este cliente aún no tiene bitácora.' : 'No hay registros en esta categoría.'}
                                    />
                                )}
                                {bitacoraState === 'ready' && visibleNotes.length > 0 && (
                                    <div className="flex flex-col">
                                        {visibleNotes.map((item, index) => {
                                            const meta = noteMeta(item.tipo)
                                            return (
                                                <div key={item.id || index} className="flex gap-3.5">
                                                    <div className="flex flex-col items-center">
                                                        <span className={`flex h-[34px] w-[34px] items-center justify-center rounded-[10px] ${meta.tone}`}>
                                                            <meta.Icon style={{ fontSize: 16 }} />
                                                        </span>
                                                        {index < visibleNotes.length - 1 && <span className="my-1 w-0.5 flex-1 bg-k-line" />}
                                                    </div>
                                                    <div className="flex min-w-0 flex-1 flex-col gap-1 pb-[18px] pt-1">
                                                        <div className="flex flex-wrap justify-between gap-x-2.5 gap-y-1">
                                                            <span className="text-sm font-extrabold">{meta.label}</span>
                                                            <span className="text-xs tabular-nums text-k-muted">{formatActivityDate(item.fecha) || '—'}</span>
                                                        </div>
                                                        {item.texto && <p className="m-0 rounded-xl border border-k-line bg-k-surface px-3 py-2.5 text-sm leading-normal text-k-ink-2">{item.texto}</p>}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        {cliente && !loading && tab === 'adjuntos' && (
                            <>
                                <GrantContent permisos={[Permisos.ADJUNTOS_W]}>
                                    <label className={`flex cursor-pointer items-center gap-3.5 rounded-2xl border-[1.5px] border-dashed border-k-line-strong bg-k-surface p-4 ${uploading ? 'opacity-60' : ''}`}>
                                        <span className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-tone-blue text-tone-blue-ink">
                                            <UploadFileOutlinedIcon />
                                        </span>
                                        <span className="flex flex-col gap-0.5">
                                            <span className="text-sm font-extrabold text-k-ink">{uploading ? 'Subiendo archivos…' : 'Subir archivos'}</span>
                                            <span className="text-xs text-k-muted">PDF, JPG, PNG o XLS</span>
                                        </span>
                                        <input type="file" multiple className="sr-only" disabled={uploading} onChange={(event) => { uploadFiles(event.target.files); event.target.value = '' }} />
                                    </label>
                                </GrantContent>
                                {isBusy(adjuntosState) && <AdjuntosSkeleton />}
                                {adjuntosError && <p className="m-0 text-sm text-k-danger-ink">{adjuntosError}</p>}
                                {adjuntosState === 'ready' && adjuntos.length === 0 && (
                                    <EmptyState title="No hay adjuntos" hint="Este cliente aún no tiene archivos." />
                                )}
                                {adjuntosState === 'ready' && adjuntos.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        {adjuntos.map((adjunto) => {
                                            const ext = fileExt(adjunto.archivo)
                                            return (
                                                <div key={adjunto.id} className="flex items-center gap-3 rounded-[14px] border border-k-line bg-k-surface px-3.5 py-3">
                                                    <span className={`flex h-11 w-9 items-end justify-center rounded-[7px] pb-[5px] text-[10px] font-extrabold ${extTone(ext)}`}>{ext}</span>
                                                    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                                        <span className="truncate text-sm font-bold">{adjunto.archivo || 'Archivo'}</span>
                                                        <span className="text-xs text-k-muted">{formatActivityDate(adjunto.fecha) || '—'}</span>
                                                    </span>
                                                    <button type="button" aria-label={`Ver ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => openFile(adjunto.path)}>
                                                        <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <button type="button" aria-label={`Descargar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-ink" onClick={() => openFile(adjunto.path)}>
                                                        <FileDownloadOutlinedIcon style={{ fontSize: 16 }} />
                                                    </button>
                                                    <GrantContent permisos={[Permisos.ADJUNTOS_D]}>
                                                        {confirmDeleteId === adjunto.id ? (
                                                            <button type="button" className="h-9 rounded-[10px] border border-k-danger px-2 text-xs font-bold text-k-danger" onClick={() => deleteAdjunto(adjunto.id)}>Eliminar</button>
                                                        ) : (
                                                            <button type="button" aria-label={`Eliminar ${adjunto.archivo}`} className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-k-line bg-transparent text-k-danger" onClick={() => setConfirmDeleteId(adjunto.id)}>
                                                                <DeleteOutlinedIcon style={{ fontSize: 16 }} />
                                                            </button>
                                                        )}
                                                    </GrantContent>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex shrink-0 items-center justify-between gap-2.5 border-t border-k-line bg-k-surface px-6 py-3.5">
                        <span className="text-xs text-k-muted">{altaSpoken ? `Alta: ${altaSpoken}` : ''}</span>
                        <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-transparent px-4 text-sm font-bold text-k-ink" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </Drawer>
            {toast && (
                <div role="status" className="fixed bottom-6 left-6 z-[60] flex items-center gap-2.5 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(10,12,20,0.3)]">
                    {toast}
                </div>
            )}
        </>
    )
}

export default CustomerDrawer
